import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowLeft, FiMapPin, FiCalendar, FiInstagram } from "react-icons/fi";
import { usePosts } from "../hooks/usePosts";
import PostCard from "../components/PostCard";
import styles from "./PostDetail.module.css";

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const { posts, loading } = usePosts();
  const post = posts.find((p) => p.id === id);

  if (loading) return null;
  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = posts
    .filter((p) => p.id !== post.id)
    .slice(0, 3);

  const formattedDate = new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <main>
      <section className={styles.hero}>
        <motion.img
          src={post.coverImage}
          alt={post.title}
          className={styles.heroImage}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Link to="/blog" className={styles.backLink}>
              <FiArrowLeft /> All Stories
            </Link>
            <div className={styles.heroMeta}>
              <span>
                <FiMapPin size={14} /> {post.location}, {post.country}
              </span>
              <span>
                <FiCalendar size={14} /> {formattedDate}
              </span>
            </div>
            <h1 className={styles.heroTitle}>{post.title}</h1>
          </motion.div>
        </div>
      </section>

      <article className={styles.article}>
        <div className={styles.articleContainer}>
          <motion.p
            className={styles.excerpt}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {post.excerpt}
          </motion.p>

          <motion.div
            className={styles.body}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {post.content.split("\n\n").map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </motion.div>

          <motion.div
            className={styles.tags}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {post.tags.map((tag) => (
              <span key={tag} className={styles.tag}>
                {tag}
              </span>
            ))}
          </motion.div>

          <motion.div
            className={styles.share}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <p className={styles.shareText}>Enjoyed this story?</p>
            <a
              href="https://instagram.com/Travelwithavara"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.shareLink}
            >
              <FiInstagram size={18} /> Follow @TravelWithAvara
            </a>
          </motion.div>
        </div>
      </article>

      {post.images.length > 0 && (
        <section className={styles.gallery}>
          <div className={styles.galleryGrid}>
            {post.images.map((img, i) => (
              <motion.div
                key={i}
                className={styles.galleryItem}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                <img src={img} alt={`${post.title} ${i + 1}`} />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <section className={styles.related}>
        <div className={styles.relatedContainer}>
          <h2 className={styles.relatedTitle}>More Stories</h2>
          <div className={styles.relatedGrid}>
            {relatedPosts.map((p, i) => (
              <PostCard key={p.id} post={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
